/**
 * One retry for a lookup the shopper's calculator depends on (DIV-022).
 *
 * `fetch` RESOLVES for every HTTP status — 204, 404, 500 all arrive as a Response
 * and are the caller's to interpret, exactly as before. It only REJECTS when the
 * request never completed: a dropped mobile connection, a DNS blip, a proxy
 * resetting the socket. Before this, one such blip on the calculator lookup or
 * the config fetch was final: the shopper got no calculator for that page view,
 * and the merchant lost the sale the calculator was there to price.
 *
 * So: a rejection — and only a rejection — is tried once more after a short
 * pause. The URL is reused byte-for-byte (BUG-011: request URLs are a parity
 * contract), and nothing changes on the path where the first request succeeds,
 * which is every page view that worked before.
 *
 * No retry when it cannot help: the page is hidden or the device says it is
 * offline. Those are the shopper leaving or having no network, and a second
 * request would only be aborted too.
 */
export const LOOKUP_RETRY_DELAY_MS = 400;

function retryCannotHelp(): boolean {
  try {
    if (typeof navigator !== "undefined" && navigator.onLine === false) return true;
    if (typeof document !== "undefined" && document.visibilityState === "hidden") return true;
  } catch {
    // If the environment cannot be read, retrying is the harmless default.
  }
  return false;
}

export async function fetchRetryingOnce(url: string): Promise<Response> {
  try {
    return await fetch(url);
  } catch (firstError) {
    if (retryCannotHelp()) throw firstError;
    await new Promise((resolve) => setTimeout(resolve, LOOKUP_RETRY_DELAY_MS));
    // A second rejection propagates as-is, so callers see the same error shape
    // (a TypeError from fetch) they always did.
    return await fetch(url);
  }
}
